import { BALANCE } from '../balance';
import { moveBoard, type BoardState } from '../board';
import type { Direction } from '../rules/contracts';

/**
 * How the bot decides.
 *
 * Pure, like everything else in `shared/`: no clock, no `Math.random()`, no
 * board mutated in place. Randomness for blunders arrives as an argument, which
 * is what keeps a bot match reproducible from its match seed — the same
 * property the README claims when it calls this "a deterministic server bot".
 *
 * The search is deliberately small. It runs on the server, once per bot move,
 * in a process that may be hosting many rooms; the point is a believable
 * opponent, not the strongest 2048 agent that can be written. Depth 3 on a
 * 4×4 board with four branches is a few hundred board evaluations, which is
 * nothing next to the 380ms the fastest difficulty waits between moves.
 */

const DIRECTIONS: readonly Direction[] = ['left', 'up', 'right', 'down'];

/**
 * Spawns are simulated with a fixed source rather than the live one.
 *
 * Two reasons, and the second is the one that matters. A search that consumed
 * the match RNG would change the tiles the *real* board goes on to spawn simply
 * because the bot thought harder — so an insane bot would be playing a
 * different game from an easy one. And planning against one representative
 * spawn keeps the search a search rather than a full expectimax over every
 * placement, which is where the cost would actually go.
 */
const FIXED_SPAWN = (): number => 0;

export interface BotMoveChoice {
  readonly direction: Direction;
  /** Evaluation of the resulting board. Higher is better. Exposed for tests. */
  readonly score: number;
}

/**
 * Rank the legal moves from this board, best first.
 *
 * Ties are broken by rotating the starting direction with the move count, which
 * is what stops a bot from oscillating between two equally-rated moves forever
 * on a board where nothing is improving.
 */
export function rankBotMoves(
  board: BoardState,
  depth: number,
  moves = 0,
): readonly BotMoveChoice[] {
  const offset = ((moves % DIRECTIONS.length) + DIRECTIONS.length) % DIRECTIONS.length;
  const ordered = [...DIRECTIONS.slice(offset), ...DIRECTIONS.slice(0, offset)];

  return ordered
    .map((direction) => {
      const move = moveBoard(board, direction, FIXED_SPAWN);
      if (!move.moved) return null;
      return {
        direction,
        score: lookahead(move.state, move.scoreGain, Math.max(1, depth) - 1),
      };
    })
    .filter((candidate): candidate is BotMoveChoice => candidate !== null)
    .sort((left, right) => right.score - left.score);
}

/**
 * Pick a move.
 *
 * `blunderChance` is applied *after* the ranking, and to a move drawn from the
 * legal ones rather than from all four. A bot that "blunders" by pressing into
 * a wall does not look like a weaker player, it looks broken; one that takes a
 * real but worse move looks like somebody not paying attention, which is what
 * an easy opponent should look like.
 */
export function chooseBotMove(
  board: BoardState,
  options: { depth: number; blunderChance: number; moves?: number; random: () => number },
): Direction | null {
  const ranked = rankBotMoves(board, options.depth, options.moves ?? 0);
  if (ranked.length === 0) return null;

  const best = ranked[0] as BotMoveChoice;
  if (ranked.length === 1 || options.blunderChance <= 0) return best.direction;

  if (options.random() >= options.blunderChance) return best.direction;

  // Any legal move except the one it would have played.
  const alternatives = ranked.slice(1);
  const pick = Math.min(
    alternatives.length - 1,
    Math.floor(options.random() * alternatives.length),
  );
  return (alternatives[pick] as BotMoveChoice).direction;
}

/** Best reachable evaluation within `depth` further moves. */
function lookahead(board: BoardState, scoreGain: number, depth: number): number {
  const immediate = evaluateBoard(board) + scoreGain * BALANCE.bot.weights.scoreGain;
  if (depth <= 0) return immediate;

  let best = -Infinity;
  for (const direction of DIRECTIONS) {
    const move = moveBoard(board, direction, FIXED_SPAWN);
    if (!move.moved) continue;
    best = Math.max(best, lookahead(move.state, move.scoreGain, depth - 1));
  }

  // A position with no legal move is a dead end, and the immediate reading
  // would flatter it. Return it as-is rather than pretending it continues.
  return best === -Infinity ? immediate : best;
}

/**
 * How good is this board?
 *
 * Four classic 2048 terms. None of them is about the score: the score follows
 * from keeping the board playable, and a heuristic that chased points directly
 * fills the grid and dies. `scoreGain` is added by the caller instead, because
 * in *this* game a merge is also an attack.
 */
export function evaluateBoard(board: BoardState): number {
  const { weights } = BALANCE.bot;
  const grid = toGrid(board);
  const size = board.size;

  const empty = size * size - board.tiles.length;

  let smoothness = 0;
  let monotonicity = 0;

  for (let row = 0; row < size; row += 1) {
    monotonicity += lineMonotonicity(grid[row] as number[]);
  }
  for (let col = 0; col < size; col += 1) {
    monotonicity += lineMonotonicity(grid.map((line) => (line as number[])[col] as number));
  }

  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      const value = (grid[row] as number[])[col] as number;
      if (value === 0) continue;
      const right = col + 1 < size ? ((grid[row] as number[])[col + 1] as number) : 0;
      const below = row + 1 < size ? ((grid[row + 1] as number[])[col] as number) : 0;
      // Distance in exponents, so 2 next to 4 counts as adjacent and 2 next to
      // 1024 does not. Negated: less difference is better.
      if (right > 0) smoothness -= Math.abs(Math.log2(value) - Math.log2(right));
      if (below > 0) smoothness -= Math.abs(Math.log2(value) - Math.log2(below));
    }
  }

  const highest = board.tiles.reduce((best, tile) => Math.max(best, tile.value), 0);
  const corners = [
    (grid[0] as number[])[0],
    (grid[0] as number[])[size - 1],
    (grid[size - 1] as number[])[0],
    (grid[size - 1] as number[])[size - 1],
  ];
  const cornered = highest > 0 && corners.includes(highest) ? 1 : 0;

  return (
    empty * weights.emptyCell +
    monotonicity * weights.monotonicity +
    smoothness * weights.smoothness +
    cornered * weights.maxTileCorner
  );
}

/**
 * How close this row or column is to running one way.
 *
 * Scored as the *smaller* of the two penalties — the board only needs to be
 * ordered in one direction to be workable, and demanding both would rate a
 * perfectly stacked row as badly as a shuffled one.
 */
function lineMonotonicity(line: readonly number[]): number {
  let increasing = 0;
  let decreasing = 0;

  for (let index = 0; index + 1 < line.length; index += 1) {
    const current = line[index] as number;
    const next = line[index + 1] as number;
    const currentRank = current > 0 ? Math.log2(current) : 0;
    const nextRank = next > 0 ? Math.log2(next) : 0;
    if (currentRank > nextRank) decreasing += nextRank - currentRank;
    else increasing += currentRank - nextRank;
  }

  return Math.max(increasing, decreasing);
}

function toGrid(board: BoardState): number[][] {
  const grid = Array.from({ length: board.size }, () => new Array<number>(board.size).fill(0));
  for (const tile of board.tiles) {
    (grid[tile.cell.row] as number[])[tile.cell.col] = tile.value;
  }
  return grid;
}
